import './ChoosePeriod.css'

import React, {Component} from 'react';
import {
    XYPlot,
    RadialChart,
    XAxis,
    YAxis,
    VerticalGridLines,
    HorizontalGridLines,
    VerticalBarSeries,
    LabelSeries,
    DiscreteColorLegend,
} from 'react-vis';

import Menu from "../Menu";
import PageHeader from "../PageHeader";

import '../../../node_modules/react-vis/dist/style.css';
import GlobalVariable from "../../GlobalVariable";
import api from "../../api";

const visColors = {
    dark: '#0d263f',
    light: '#cdcdcd',
    bright: '#e40136',
}

type PeriodKey = 'week' | 'month' | 'quarter' | 'year' | 'two_years';

type TrendValue = number | null;
type TrendMode = 'change' | 'points';

type ChartPoint = {
    x: string;
    y: number;
    label: string;
};

type DonutPoint = {
    angle: number;
    label: string;
};

type CompanyOption = {
    value: string;
    label: string;
};

type PeriodOption = {
    value: PeriodKey;
    label: string;
};

type Summary = {
    revenue: number;
    appointments: number;
    avgBill: number;
    registeredVisits: number;
    repeatVisits: number;
    daysWorked: number;
    registeredShare: number | null;
    repeatShare: number | null;
};

type Comparison = {
    revenue: TrendValue;
    appointments: TrendValue;
    avgBill: TrendValue;
    repeatShare: TrendValue;
    registeredShare: TrendValue;
    daysWorked: TrendValue;
};

type RegistrationBars = {
    registered: ChartPoint[];
    unregistered: ChartPoint[];
    label: string;
};

type Meta = {
    hasData: boolean;
    periodLabel: string;
    lastAvailableDate: string | null;
    sharesNote: string;
};

type AnalyticsState = {
    loading: boolean;
    error: string;
    selectedPeriod: PeriodKey;
    selectedCompanyId: string;
    periodOptions: PeriodOption[];
    companyOptions: CompanyOption[];
    summary: Summary;
    comparison: Comparison;
    revenueChart: ChartPoint[];
    repeatChart: DonutPoint[];
    registrationBars: RegistrationBars;
    meta: Meta;
    message: string | null;
};

const DEFAULT_PERIOD_OPTIONS: PeriodOption[] = [
    {value: 'week', label: '1 неделя'},
    {value: 'month', label: '1 месяц'},
    {value: 'quarter', label: '3 месяца'},
    {value: 'year', label: '1 год'},
    {value: 'two_years', label: '2 года'},
];

const DEFAULT_SUMMARY: Summary = {
    revenue: 0,
    appointments: 0,
    avgBill: 0,
    registeredVisits: 0,
    repeatVisits: 0,
    daysWorked: 0,
    registeredShare: null,
    repeatShare: null,
};

const DEFAULT_COMPARISON: Comparison = {
    revenue: null,
    appointments: null,
    avgBill: null,
    repeatShare: null,
    registeredShare: null,
    daysWorked: null,
};

const DEFAULT_REGISTRATION_BARS: RegistrationBars = {
    registered: [],
    unregistered: [],
    label: 'Визиты',
};

const DEFAULT_META: Meta = {
    hasData: false,
    periodLabel: '1 неделя',
    lastAvailableDate: null,
    sharesNote: 'Доли считаются по визитам за выбранный период.',
};

function formatNumber(value: number): string {
    return value.toLocaleString('ru-RU');
}

function formatMoney(value: number): string {
    return `${formatNumber(value)} ₽`;
}

function formatPercent(value: number | null): string {
    if (value === null || Number.isNaN(value)) {
        return '—';
    }

    return `${value.toLocaleString('ru-RU', {maximumFractionDigits: 1})}%`;
}

function formatTrend(value: TrendValue, mode: TrendMode = 'change'): string {
    if (value === null || Number.isNaN(value)) {
        return 'Нет данных для сравнения';
    }

    if (value === 0) {
        return 'Без изменений к прошлому периоду';
    }

    const prefix = value > 0 ? '+' : '';
    const suffix = mode === 'points' ? 'п.п. к прошлому периоду' : '% к прошлому периоду';

    return `${prefix}${value.toLocaleString('ru-RU', {maximumFractionDigits: 1})} ${suffix}`;
}

function trendClass(value: TrendValue): string {
    if (value === null || Number.isNaN(value) || value === 0) {
        return 'neutral';
    }

    return value > 0 ? 'up' : 'down';
}

export class Analytics extends Component<any, AnalyticsState> {
    constructor(props: any) {
        super(props);
        window.scrollTo(0, 0);
        this.state = {
            loading: true,
            error: '',
            selectedPeriod: 'week',
            selectedCompanyId: 'all',
            periodOptions: DEFAULT_PERIOD_OPTIONS,
            companyOptions: [{value: 'all', label: 'Все салоны'}],
            summary: DEFAULT_SUMMARY,
            comparison: DEFAULT_COMPARISON,
            revenueChart: [],
            repeatChart: [],
            registrationBars: DEFAULT_REGISTRATION_BARS,
            meta: DEFAULT_META,
            message: null,
        };
    }

    componentDidMount() {
        this.loadAnalytics();
    }

    loadAnalytics = () => {
        let urlAPI = GlobalVariable.staffBack() + '/mobile/analytics/staff';
        const data = {
            period: this.state.selectedPeriod,
            companyId: this.state.selectedCompanyId === 'all' ? null : Number(this.state.selectedCompanyId),
        };

        this.setState({
            loading: true,
            error: '',
        });

        api({
            url: urlAPI,
            headers: {'Content-Type': 'application/json'},
            method: "POST",
            data: data,
            withCredentials: true,
        })
            .then(result => {
                const payload = result.data || {};
                this.setState({
                    loading: false,
                    error: '',
                    selectedPeriod: payload.selectedPeriod || this.state.selectedPeriod,
                    selectedCompanyId: payload.selectedCompanyId || 'all',
                    periodOptions: payload.periods && payload.periods.length ? payload.periods : this.state.periodOptions,
                    companyOptions: payload.companyOptions && payload.companyOptions.length ? payload.companyOptions : [{value: 'all', label: 'Все салоны'}],
                    summary: payload.summary || DEFAULT_SUMMARY,
                    comparison: payload.comparison || DEFAULT_COMPARISON,
                    revenueChart: payload.charts && payload.charts.revenue ? payload.charts.revenue : [],
                    repeatChart: payload.charts && payload.charts.repeatShare ? payload.charts.repeatShare : [],
                    registrationBars: payload.charts && payload.charts.registrationShare ? payload.charts.registrationShare : DEFAULT_REGISTRATION_BARS,
                    meta: payload.meta || DEFAULT_META,
                    message: payload.message || null,
                })
            }).catch(error => {
            this.setState({
                loading: false,
                error: 'Не удалось загрузить аналитику. Попробуйте обновить экран чуть позже.',
            });
        });
    }

    handlePeriodChange = (period: PeriodKey) => {
        if (period === this.state.selectedPeriod) {
            return;
        }

        this.setState({
            selectedPeriod: period,
        }, this.loadAnalytics);
    }

    handleCompanyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({
            selectedCompanyId: event.target.value,
        }, this.loadAnalytics);
    }

    renderStatCard(title: string, value: string, trend: TrendValue, subtitle: string, trendMode: TrendMode = 'change') {
        return (
            <div className='analytics-summary-item'>
                <div className='chio-card p-3 analytics-stat-card'>
                    <div className='txt-middle analytics-stat-title'>{title}</div>
                    <div className='analytics-stat-value mt-2'>{value}</div>
                    <div className={`analytics-trend ${trendClass(trend)}`}>{formatTrend(trend, trendMode)}</div>
                    <div className='analytics-note mt-2'>{subtitle}</div>
                </div>
            </div>
        );
    }

    renderFilters() {
        return (
            <div className='ml-3 mr-3 mt-4 mb-3'>
                <div className='d-flex choose-period analytics-filters'>
                    {this.state.periodOptions.map((period) => (
                        <button
                            key={period.value}
                            type='button'
                            className={`chio-card pt-1 pb-1 pr-2 pl-2 mr-1 ${this.state.selectedPeriod === period.value ? 'active' : ''}`}
                            onClick={() => this.handlePeriodChange(period.value)}
                        >
                            {period.label}
                        </button>
                    ))}
                </div>

                {this.state.companyOptions.length > 1 ? (
                    <div className='mt-3'>
                        <div className='analytics-note mb-2'>Салон</div>
                        <select
                            className='form-select txt-middle analytics-select'
                            value={this.state.selectedCompanyId}
                            onChange={this.handleCompanyChange}
                        >
                            {this.state.companyOptions.map((company) => (
                                <option key={company.value} value={company.value}>
                                    {company.label}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}
            </div>
        );
    }

    renderContent() {
        const {summary, comparison, revenueChart, repeatChart, registrationBars, meta, message} = this.state;
        const showRevenueLabels = revenueChart.length > 0 && revenueChart.length <= 12;
        const revenueChartWidth = Math.max(300, revenueChart.length * (showRevenueLabels ? 78 : 52));
        const registrationDomain = [registrationBars.label || 'Визиты'];

        if (this.state.loading) {
            return (
                <div className='chio-card ml-3 mr-3 p-3 mb-3'>
                    Загружаем аналитику...
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className='chio-card ml-3 mr-3 p-3 mb-3 text-danger'>
                    {this.state.error}
                </div>
            );
        }

        if (!meta.hasData) {
            return (
                <div className='chio-card ml-3 mr-3 p-4 mb-3 analytics-empty'>
                    <div className='txt-large mb-2'>{message || 'За выбранный период данных пока нет.'}</div>
                    <div className='txt-middle dark-textc'>Попробуйте переключить период или выбрать другой салон.</div>
                    {meta.lastAvailableDate ? (
                        <div className='analytics-note mt-3'>Последние данные: {meta.lastAvailableDate}</div>
                    ) : null}
                </div>
            );
        }

        return (
            <>
                <div className='chio-card ml-3 mr-3 p-3 mb-3 analytics-info-card'>
                    Показываем только подтвержденные персональные метрики без сравнений с салоном и городом, пока для них не подключены живые данные.
                </div>

                <div className='analytics-summary-grid ml-3 mr-3'>
                    {this.renderStatCard('Выручка', formatMoney(summary.revenue), comparison.revenue, `За ${meta.periodLabel.toLowerCase()}`)}
                    {this.renderStatCard('Средний чек', formatMoney(summary.avgBill), comparison.avgBill, 'Считаем по оплаченным визитам')}
                    {this.renderStatCard('Записи', formatNumber(summary.appointments), comparison.appointments, 'Количество визитов в периоде')}
                    {this.renderStatCard('Повторные визиты', formatPercent(summary.repeatShare), comparison.repeatShare, 'Доля визитов повторных клиентов', 'points')}
                    {this.renderStatCard('Зарегистрированные визиты', formatPercent(summary.registeredShare), comparison.registeredShare, 'Доля визитов клиентов с профилем', 'points')}
                    {this.renderStatCard('Дней с визитами', formatNumber(summary.daysWorked), comparison.daysWorked, 'Дни, когда были оплаченные визиты')}
                </div>

                <div className='chio-card ml-3 mr-3 p-3 mb-3'>
                    <div className='analytics-section-head'>
                        <div>
                            <h2 className='txt-large mt-0 mb-1'>Динамика выручки</h2>
                            <div className='analytics-section-meta'>Оплаченные визиты за {meta.periodLabel.toLowerCase()}</div>
                        </div>
                        {meta.lastAvailableDate ? (
                            <div className='analytics-section-meta'>Последние данные: {meta.lastAvailableDate}</div>
                        ) : null}
                    </div>

                    <div className='analytics-chart-scroll mt-3'>
                        <XYPlot xType="ordinal" height={240} width={revenueChartWidth} color={visColors.dark}>
                            <VerticalGridLines/>
                            <HorizontalGridLines/>
                            <XAxis/>
                            <YAxis left={16}/>
                            <VerticalBarSeries data={revenueChart}/>
                            {showRevenueLabels ? (
                                <LabelSeries
                                    data={revenueChart}
                                    labelAnchorX="middle"
                                    labelAnchorY="text-before-edge"
                                    style={{
                                        fill: visColors.bright,
                                        fontSize: 12,
                                        fontWeight: 'bold'
                                    }}
                                />
                            ) : null}
                        </XYPlot>
                    </div>

                    <div className='analytics-note mt-2'>По оси X показываем дни, недели или месяцы в зависимости от выбранного периода.</div>
                </div>

                <div className='analytics-share-grid ml-3 mr-3'>
                    <div className='analytics-share-item'>
                        <div className='chio-card p-3 mb-3'>
                            <h2 className='txt-large mt-0 mb-2'>Доля повторных визитов</h2>
                            <div className='analytics-note mb-2'>{meta.sharesNote}</div>
                            <div style={{maxWidth: '220px', margin: '0 auto'}}>
                                <RadialChart
                                    height={220}
                                    width={220}
                                    colorType="category"
                                    colorRange={[visColors.dark, visColors.light]}
                                    data={repeatChart}
                                    labelsRadiusMultiplier={0.78}
                                    labelsStyle={{
                                        fill: visColors.bright,
                                        fontSize: 12
                                    }}
                                    showLabels
                                />
                            </div>
                            <DiscreteColorLegend
                                colors={[visColors.dark, visColors.light]}
                                items={['Повторные', 'Новые']}
                                orientation="horizontal"
                            />
                        </div>
                    </div>

                    <div className='analytics-share-item'>
                        <div className='chio-card p-3 mb-3'>
                            <h2 className='txt-large mt-0 mb-2'>Доля зарегистрированных визитов</h2>
                            <div className='analytics-note mb-2'>{meta.sharesNote}</div>
                            <div className='analytics-chart-scroll'>
                                <XYPlot xType="ordinal" width={300} height={260} stackBy="y" xDomain={registrationDomain} yDomain={[0, 100]}>
                                    <VerticalGridLines/>
                                    <HorizontalGridLines/>
                                    <XAxis/>
                                    <YAxis/>
                                    <VerticalBarSeries data={registrationBars.registered} color={visColors.dark}/>
                                    <VerticalBarSeries data={registrationBars.unregistered} color={visColors.light}/>
                                    <LabelSeries
                                        data={registrationBars.registered}
                                        labelAnchorX="middle"
                                        labelAnchorY="text-before-edge"
                                        style={{
                                            fill: visColors.bright,
                                            fontSize: 12
                                        }}
                                    />
                                    <LabelSeries
                                        data={registrationBars.unregistered}
                                        labelAnchorX="middle"
                                        labelAnchorY="text-before-edge"
                                        style={{
                                            fill: visColors.bright,
                                            fontSize: 12
                                        }}
                                    />
                                </XYPlot>
                            </div>
                            <DiscreteColorLegend
                                colors={[visColors.dark, visColors.light]}
                                items={['Зарегистрированные', 'Незарегистрированные']}
                                orientation="horizontal"
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    render() {
        return (
            <div className='main analytic'>
                <div className='mx-w-content'>
                    <PageHeader pageTitle='Аналитика' backBtnUrl='/'/>
                    <div className='pt-header'></div>

                    {this.renderFilters()}
                    {this.renderContent()}
                </div>
                <Menu></Menu>
            </div>
        );
    }
}

export default Analytics;
